import { useEffect, useState } from 'react';
import {
  addTimeEntry,
  deleteTimeEntry,
  listTimeEntries,
  startTimer,
  stopTimer,
  type TimeEntry,
} from '../../lib/tasks';
import { useAuth } from '../../lib/auth';
import { memberName } from '../../lib/users';
import { formatClock, formatDuration, parseDuration } from '../../lib/duration';
import { dateInputToInstant, todayInputValue } from '../../lib/taskDates';
import { Alert, Button, Input, SkeletonRows } from '../ui';
import { Avatar } from './TaskBadges';
import { PlayIcon, StopIcon, TrashIcon } from '../icons';

/**
 * Elapsed seconds for a running entry.
 *
 * Derived from the wall clock on every tick rather than incremented: background
 * tabs clamp and often suspend intervals, and the machine can sleep, so a
 * counter would silently drift behind the truth. It also makes a page reload
 * free -- `startedAt` comes from the server, so the timer resumes exactly where
 * it was rather than restarting from zero.
 */
function useElapsedSeconds(startedAt: string | null): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!startedAt) return;
    setNow(Date.now()); // Resync immediately; do not wait a full second.
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [startedAt]);

  if (!startedAt) return 0;
  // Clamped at zero: the server's clock and the browser's can disagree slightly.
  return Math.max(0, Math.floor((now - Date.parse(startedAt)) / 1000));
}

export function TaskTimeLog({
  taskId,
  onTotalChange,
}: {
  taskId: string;
  onTotalChange: (seconds: number) => void;
}) {
  const { user } = useAuth();
  const [entries, setEntries] = useState<TimeEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manualAmount, setManualAmount] = useState('');
  const [manualDate, setManualDate] = useState(todayInputValue);
  const [manualNote, setManualNote] = useState('');
  const [manualError, setManualError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);

    listTimeEntries(taskId, controller.signal)
      .then((data) => {
        setEntries(data.entries);
        setError(null);
      })
      .catch((requestError: unknown) => {
        if (controller.signal.aborted) return;
        setError(requestError instanceof Error ? requestError.message : 'Could not load time.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [taskId]);

  // Only the caller's own timer drives the live display -- someone else's
  // running entry is shown in the list but is not this user's to stop.
  const running = entries.find((entry) => entry.running && entry.user?.id === user?.id) ?? null;
  const elapsed = useElapsedSeconds(running?.startedAt ?? null);
  const finished = entries.reduce((total, entry) => total + (entry.seconds ?? 0), 0);
  const total = finished + elapsed;

  function apply(next: TimeEntry[]) {
    setEntries(next);
    onTotalChange(next.reduce((sum, entry) => sum + (entry.seconds ?? 0), 0));
  }

  async function toggle() {
    setBusy(true);
    setError(null);
    try {
      if (running) {
        const { entry } = await stopTimer(taskId);
        apply(entries.map((existing) => (existing.id === entry.id ? entry : existing)));
      } else {
        const { entry } = await startTimer(taskId);
        apply([entry, ...entries]);
      }
    } catch (toggleError: unknown) {
      setError(toggleError instanceof Error ? toggleError.message : 'Could not update the timer.');
    } finally {
      setBusy(false);
    }
  }

  async function logManual() {
    const seconds = parseDuration(manualAmount);
    if (seconds === null) {
      setManualError('Try 90m, 1h30, or 1:30.');
      return;
    }

    setManualError(null);
    try {
      const { entry } = await addTimeEntry(taskId, {
        startedAt: dateInputToInstant(manualDate),
        seconds,
        note: manualNote.trim() || null,
      });
      setManualAmount('');
      setManualNote('');
      setShowManual(false);
      apply([entry, ...entries]);
    } catch (logError: unknown) {
      setManualError(logError instanceof Error ? logError.message : 'Could not log that time.');
    }
  }

  async function remove(entryId: string) {
    try {
      await deleteTimeEntry(taskId, entryId);
      apply(entries.filter((entry) => entry.id !== entryId));
    } catch (deleteError: unknown) {
      setError(deleteError instanceof Error ? deleteError.message : 'Could not delete that entry.');
    }
  }

  return (
    <section>
      <div className="flex items-baseline justify-between">
        <h3 className="text-dense font-semibold tracking-tight text-stone-700">Time</h3>
        {total > 0 ? (
          <span className="text-meta text-stone-500">{formatDuration(total)} logged</span>
        ) : null}
      </div>

      {error ? (
        <div className="mt-2">
          <Alert>{error}</Alert>
        </div>
      ) : null}

      <div className="mt-1.5 flex items-center gap-2">
        <Button
          compact
          variant={running ? 'danger' : 'primary'}
          disabled={busy}
          onClick={() => void toggle()}
        >
          {running ? <StopIcon /> : <PlayIcon />}
          {running ? 'Stop' : 'Start'}
        </Button>

        {running ? (
          // tabular-nums so the digits do not jitter as they tick.
          <span className="font-mono text-dense tabular-nums text-stone-700">
            {formatClock(elapsed)}
          </span>
        ) : null}

        <Button compact variant="ghost" onClick={() => setShowManual((value) => !value)}>
          Add time
        </Button>
      </div>

      {showManual ? (
        <div className="mt-1.5 space-y-1.5 rounded-xl border border-stone-200 bg-stone-50 p-2.5">
          <div className="flex gap-2">
            <Input
              compact
              value={manualAmount}
              placeholder="1h 30m"
              aria-label="Duration"
              onChange={(event) => setManualAmount(event.target.value)}
            />
            <Input
              compact
              type="date"
              value={manualDate}
              aria-label="Date"
              onChange={(event) => setManualDate(event.target.value)}
            />
          </div>
          <Input
            compact
            value={manualNote}
            placeholder="Note (optional)"
            aria-label="Note"
            onChange={(event) => setManualNote(event.target.value)}
          />
          {manualError ? <p className="text-dense text-red-700">{manualError}</p> : null}
          <Button compact variant="primary" onClick={() => void logManual()}>
            Log time
          </Button>
        </div>
      ) : null}

      {loading ? (
        <SkeletonRows count={3} rowClassName="h-5" />
      ) : entries.length === 0 ? (
        <p className="mt-2 text-dense text-stone-500">No time logged yet.</p>
      ) : (
        <ul className="mt-2 space-y-0.5">
          {entries.map((entry) => (
            <li key={entry.id} className="flex items-center gap-2 text-dense">
              <Avatar
                label={memberName(entry.user)}
                pictureUrl={entry.user?.pictureUrl}
                className="h-4 w-4"
              />
              <span className="w-16 shrink-0 tabular-nums text-stone-700">
                {entry.running ? 'running' : formatDuration(entry.seconds ?? 0)}
              </span>
              <span className="shrink-0 text-meta text-stone-500">
                {new Date(entry.startedAt).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric',
                })}
              </span>
              <span className="min-w-0 flex-1 truncate text-meta text-stone-500">{entry.note}</span>
              {entry.user?.id === user?.id ? (
                <button
                  type="button"
                  aria-label="Delete time entry"
                  className="shrink-0 rounded p-0.5 text-stone-500 transition hover:bg-red-50 hover:text-red-700"
                  onClick={() => void remove(entry.id)}
                >
                  <TrashIcon className="h-3.5 w-3.5" />
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
